import { Facility } from '@reliance/types/models/Facility';
import React, { useState } from 'react';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import api from '../common/api';
import ConfirmModal from '../common/dialogs/ConfirmModal';
import { useUser } from '../contexts/UserContext';
import { clearFacilityCache } from '../hooks/Facility';
import EditModal from './EditModal';

type FacilityPageHeaderProps = {
  facility: Facility | null;
  onRefresh(): void | Promise<unknown>;
};

const friendlyTimezone = (facility: Facility) => {
  switch (facility.timezone) {
    case 'America/Chicago':
      return 'Central';
    case 'America/New_York':
      return 'Eastern';
    case 'America/Denver':
      return 'Mountain';
    case 'America/Phoenix':
      return 'Arizona';
    case 'America/Los_Angeles':
      return 'Pacific';
    default:
      return facility.timezone;
  }
};

const FacilityPageHeader: React.FC<FacilityPageHeaderProps> = (props) => {
  const { facility } = props;
  const user = useUser();

  const [disabling, setDisabling] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [editing, setEditing] = useState(false);

  const disable = () => {
    setDisabling(true);
  };

  const enable = () => {
    setEnabling(true);
    api
      .put(`/facility/${facility!.id}`, { active: true })
      .then(clearFacilityCache)
      .then(props.onRefresh)
      .finally(() => setEnabling(false));
  };

  const edit = () => {
    setEditing(true);
  };

  return (
    <Row className="mb-1">
      <Col xs={12} sm={7} md={7} lg={8}>
        <h1 className="page-title txt-color-blueDark">
          <i className="fas fa-sm fa-fw fa-building"></i>
          {facility ? (
            <>
              {facility.name}
              {facility.externalId && <span>&nbsp;(External ID: {facility.externalId})</span>}
            </>
          ) : (
            'Loading...'
          )}
        </h1>
      </Col>

      <Col xs={12} sm={5} md={5} lg={4}>
        {facility && (
          <ul className="sparks">
            <li className="sparks-info">
              <h5>
                Location
                <span>
                  {facility.city}, {facility.state}
                </span>
              </h5>
            </li>
            <li className="sparks-info">
              <h5>
                Timezone
                <span>{friendlyTimezone(facility)}</span>
              </h5>
            </li>
            {user.hasPermission('facility_disable') && (
              <li className="sparks-info">
                <h5>
                  {facility.active ? (
                    <Button variant="danger" disabled={disabling} onClick={disable}>
                      Disable
                    </Button>
                  ) : (
                    <Button variant="success" disabled={enabling} onClick={enable}>
                      Enable
                    </Button>
                  )}
                </h5>
              </li>
            )}
            {user.hasPermission('facility_manage') && (
              <li className="sparks-info">
                <h5>
                  <Button variant="info" onClick={edit}>
                    Edit
                  </Button>
                </h5>
              </li>
            )}
            {facility?.siteUrl && (
              <li className="sparks-info">
                <h5>
                  <a
                    href={facility.siteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-info"
                    title="Open Relcore Site">
                    <i className="fas fa-globe" />
                  </a>
                </h5>
              </li>
            )}
          </ul>
        )}
      </Col>

      {disabling && facility && (
        <ConfirmModal
          title="Disable Facility"
          show={true}
          onAccept={() =>
            api
              .put(`/facility/${facility.id}`, { active: false })
              .then(props.onRefresh)
              .finally(() => setDisabling(false))
          }
          onClose={() => setDisabling(false)} >
          Are you sure you wish to fully disable this facility record?
          <br />
          <br />
          <strong>This removes all services from this facility! All inmates will also have devices unassigned.</strong>
        </ConfirmModal>
      )}

      {editing && facility && (
        <EditModal
          facility={facility}
          onSave={(saved) => {
            if (saved) {
              void props.onRefresh();
            }
            setEditing(false);
          }}
        />
      )}
    </Row>
  );
};

export default FacilityPageHeader;
